import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowLeft, FiFileText, FiCalendar, FiShield, FiAlertTriangle } = FiIcons;

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/')}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="text-gray-600" />
            </button>
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiFileText} className="text-blue-600 text-2xl" />
              <h1 className="text-3xl font-bold text-gray-900">Brukervilkår</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg overflow-hidden"
        >
          <div className="p-8 space-y-8">
            {/* Introduction */}
            <section>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                <h2 className="text-xl font-semibold text-blue-900 mb-3">Velkommen til HMSvarsling</h2>
                <p className="text-blue-800 leading-relaxed">
                  Disse brukervilkårene regulerer din bruk av HMSvarsling-plattformen, et varemerke eiet av Employer Brand AS.
                  Ved å registrere deg eller bruke tjenesten aksepterer du disse vilkårene.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <SafeIcon icon={FiCalendar} className="text-blue-600 text-2xl mx-auto mb-2" />
                  <div className="text-sm text-gray-600">Sist oppdatert</div>
                  <div className="font-medium">1. juni 2024</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <SafeIcon icon={FiShield} className="text-green-600 text-2xl mx-auto mb-2" />
                  <div className="text-sm text-gray-600">Gyldig fra</div>
                  <div className="font-medium">1. januar 2024</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <SafeIcon icon={FiFileText} className="text-purple-600 text-2xl mx-auto mb-2" />
                  <div className="text-sm text-gray-600">Versjon</div>
                  <div className="font-medium">2.1</div>
                </div>
              </div>
            </section>

            {/* Service Description */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Tjenestebeskrivelse</h2>
              <p className="text-gray-700 mb-4">
                HMSvarsling tilbyr en digital plattform for trygg varsling og HMS-rapportering.
              </p>
            </section>

            {/* Data Processing Agreement Clause */}
            <section className="bg-purple-50 border border-purple-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-purple-900 mb-3">Databehandleravtale (GDPR)</h2>
              <p className="text-purple-800 leading-relaxed mb-3">
                For bedriftskunder innebærer bruken av tjenesten behandling av personopplysninger. 
                I henhold til GDPR Artikkel 28 kreves det en egen <strong>Databehandleravtale (DPA)</strong> mellom Behandlingsansvarlig (Kunden) og Databehandler (Oss).
              </p>
              <ul className="list-disc pl-5 text-sm text-purple-800 space-y-1">
                <li>Databehandleravtalen er et eget juridisk dokument som må signeres/aksepteres.</li>
                <li>Du finner avtalen tilgjengelig for digital signering under <strong>Innstillinger &rarr; Databehandleravtale</strong> i bedriftsportalen etter innlogging.</li>
                <li>Inntil avtalen er signert, reguleres databehandlingen midlertidig av disse brukervilkårene og vår personvernerklæring.</li>
              </ul>
            </section>

            {/* User Obligations */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Brukerforpliktelser</h2>
              <div className="space-y-4">
                <div className="border-l-4 border-green-500 bg-green-50 p-4 rounded-r-lg">
                  <h3 className="font-semibold text-green-900 mb-2">Du forplikter deg til å:</h3>
                  <ul className="text-green-800 space-y-1 text-sm">
                    <li>• Oppgi korrekte og oppdaterte opplysninger</li>
                    <li>• Bruke tjenesten i henhold til norsk lov</li>
                    <li>• Respektere andre brukeres rettigheter</li>
                    <li>• Holde påloggingsinformasjon konfidensiell</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Contact */}
            <section className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-blue-900 mb-4">Spørsmål om vilkårene?</h2>
              <div className="space-y-2 text-blue-800">
                <p><strong>Employer Brand AS</strong></p>
                <p>Org.nr: 925 182 672 MVA</p>
                <p>E-post: support@hmsvarsling.no</p>
                <p>Telefon: 40 55 19 89</p>
                <p>Adresse: Motzfeldts gate 21, 0561 Oslo</p>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsOfService;